import { Button, Col, Divider, Row, message } from "antd";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ZoneContext from "../../../../context/wms/zone/context";
import ZoneContextProvider from "../../../../context/wms/zone/provider";
import { ICreateZone } from "../../../../models/wms/zone/request";
import EndPoints from "../../../../services/end-points";
import { execute } from "../../../../utils/helpers/execute";
import { languages } from "../../../../utils/helpers/constants";
import { getTranslations } from "../../../../utils/helpers/translations";
import FieldBuilder from "../../../form-components/field-builder";
import Spin from "../../../general/antd/spin";
import styles from "./style.module.scss";
import useHierarchicalDropdown, { ChainItem } from "../../../../hooks/wms/use-hierarchical-dropdown";
import PreviousChainDisplay from "../../shared/previous-chain-display";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { details, actions, loading } = useContext(ZoneContext);
  const { id } = useParams();

  const methods = useForm<ICreateZone>({
    defaultValues: {
      name: {},
      zoneTypeId: undefined,
      storeId: undefined,
      pickSequence: undefined,
      description: "",
      isActive: false,
    },
  });

  const { handleSubmit, reset, setValue } = methods;

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Use hierarchical dropdown hook with progressive visibility
  const {
    stores,
    loadingStores,
    zoneTypes,
    loadingZoneTypes,
    previousChain,
    capturePreviousChain,
    clearPreviousChain,
    resetFromLevel,
  } = useHierarchicalDropdown({ debounceMs: 300 });

  // Capture previous chain on mount in edit mode
  useEffect(() => {
    if (id && details) {
      const chain: ChainItem[] = [];
      if (details.store?.name) {
        chain.push({ label: details.store.name, value: details.storeId });
      } else if (details.storeId) {
        chain.push({ label: `Store ${details.storeId}`, value: details.storeId });
      }
      if (details.zoneType?.name) {
        const zoneTypeName = typeof details.zoneType.name === 'object' && details.zoneType.name !== null
          ? ((details.zoneType.name as any).en || (details.zoneType.name as any).ar || '')
          : details.zoneType.name;
        chain.push({ label: zoneTypeName, value: details.zoneTypeId });
      } else if (details.zoneTypeId) {
        chain.push({ label: `Zone Type ${details.zoneTypeId}`, value: details.zoneTypeId });
      }
      if (chain.length > 0) {
        capturePreviousChain(chain);
      }
    }
  }, [id, details]);

  // Handle store change - reset children
  const handleStoreChange = (storeId: number | undefined) => {
    if (storeId && storeId !== details?.storeId) {
      resetFromLevel('store');
      setValue('zoneTypeId', undefined as any);
    }
  };

  const onSubmit = async (data: ICreateZone) => {
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        if (details) {
          await EndPoints.wmsLayout.updateZone(details.id, data);
        } else {
          await EndPoints.wmsLayout.createZone(data);
        }
        navigate("/wms/zones");
      },
      fallback: (error) => {
        message.error(t('operation_failed'));
      },
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  // Reset form when details loads
  useEffect(() => {
    if (details) {
      reset({
        name: getTranslations(details?.translations?.name) ?? {},
        zoneTypeId: details.zoneTypeId,
        storeId: details.storeId,
        pickSequence: details.pickSequence ?? undefined,
        description: details.description ?? "",
        isActive: details.isActive ?? false,
      });
    }
  }, [details, reset]);

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_zone") : t("add_zone")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") && id ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <>
              {previousChain && (
                <PreviousChainDisplay
                  chain={previousChain}
                  onClear={clearPreviousChain}
                />
              )}
              <div className={styles.children}>
                <Row gutter={[16, 16]}>
                  {languages.map((lang) => (
                    <Col key={lang.code} xs={24} md={12}>
                      <FieldBuilder
                        name={`name[${lang.code}]`}
                        label={`${t("zone_name")} (${lang.name})`}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>
                  ))}
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="storeId"
                      label={t("select_store")}
                      input={{
                        type: "select",
                        loading: loadingStores,
                        allowSearch: true,
                        options: stores.map((s) => ({
                          label: s.label,
                          value: s.id,
                        })),
                        onChange: handleStoreChange,
                      }}
                      rules={{ required: true }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="zoneTypeId"
                      label={t("zone_type")}
                      input={{
                        type: "select",
                        loading: loadingZoneTypes,
                        allowSearch: true,
                        options: zoneTypes.map((zt) => ({
                          label: zt.label,
                          value: zt.id,
                        })),
                      }}
                      rules={{ required: true }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="pickSequence"
                      label={t("pick_sequence")}
                      input={{ type: "number" }}
                      rules={{ required: false }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="description"
                      label={t("description")}
                      input={{ type: "text" }}
                      rules={{ required: false }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <FieldBuilder
                      name="isActive"
                      label={t("is_active")}
                      input={{ type: "switch" }}
                      rules={{ required: false }}
                    />
                  </Col>
                </Row>
              </div>
            </>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const ZoneForm = () => {
  return (
    <ZoneContextProvider>
      <Form />
    </ZoneContextProvider>
  );
};

export default ZoneForm;
